import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Calendar, TrendingUp, AlertCircle } from "lucide-react";

const BUCKET_TONE = {
  expired: "bg-rose-500",
  "0-30d": "bg-amber-500",
  "31-90d": "bg-amber-400",
  "91-180d": "bg-sky-500",
  "181-365d": "bg-slate-400",
  "365d+": "bg-emerald-500",
};
const BUCKET_LABEL = {
  expired: "Expired warranty",
  "0-30d": "≤ 30 days",
  "31-90d": "31–90 days",
  "91-180d": "91–180 days",
  "181-365d": "181–365 days",
  "365d+": "> 1 year",
};

export default function RefreshForecast() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/refresh-forecast")
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total_forecast_value || 0); })
      .finally(() => setLoading(false));
  }, []);

  const grand = items.reduce((s, r) => s + r.count, 0) || 1;

  return (
    <div className="space-y-6 max-w-[1400px]">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Intelligence · Planning</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Refresh Forecast</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Anticipate warranty expiry, refresh cycles and budget commitments across your fleet.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryTile
          label="Total refresh value"
          value={`USD ${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          hint="Sum of purchase value of devices with warranty expiring in the next 365 days"
          icon={TrendingUp}
        />
        <SummaryTile
          label="Devices in fleet"
          value={grand.toLocaleString()}
          hint="Total with a tracked warranty end date"
          icon={Calendar}
        />
        <SummaryTile
          label="Devices past warranty"
          value={(items.find((i) => i.bucket === "expired")?.count || 0).toLocaleString()}
          hint="No manufacturer coverage — consider refresh or extended support"
          icon={AlertCircle}
          tone="red"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-4">Warranty distribution</div>
        {loading ? (
          <div className="text-sm text-slate-500">Loading forecast…</div>
        ) : (
          <div className="space-y-3">
            {items.map((b) => {
              const pct = grand ? Math.round((b.count / grand) * 100) : 0;
              return (
                <div key={b.bucket}>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${BUCKET_TONE[b.bucket]}`} />
                      <span className="text-slate-800">{BUCKET_LABEL[b.bucket]}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="tabular-nums text-slate-700 font-medium">{b.count.toLocaleString()} devices</span>
                      <span className="tabular-nums">USD {b.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${BUCKET_TONE[b.bucket]}`} style={{ width: `${Math.max(2, pct)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-slate-900 text-slate-200 rounded-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-amber-400">Renewal planning</div>
            <div className="mt-1 font-heading text-lg text-white">Ready for budget conversations.</div>
          </div>
          <div className="md:col-span-2 text-sm text-slate-300 leading-relaxed">
            Use this view to align procurement cadence with warranty roll-off, identify refresh-eligible cohorts by
            country and model, and quantify the residual value approaching disposal. Export to your finance system to
            protect budget for the coming fiscal year.
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, hint, icon: Icon, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${tone === "red" ? "text-rose-500" : "text-slate-400"}`} strokeWidth={1.5} />}
      </div>
      <div className={`mt-2 text-2xl font-heading font-bold tracking-tighter tabular-nums ${tone === "red" ? "text-rose-700" : "text-slate-900"}`}>
        {value}
      </div>
      <div className="mt-1 text-[11px] text-slate-500 leading-relaxed">{hint}</div>
    </div>
  );
}
